const API_URL = (import.meta as any).env.VITE_API_URL ?? ''

function getTokenHeader() {
    const token = getToken()
    if (!token) throw new Error('No token found')
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
}

export interface LoginResponse {
    token: string
    school: {
        id: number
        name: string
        username: string
        firebaseUrl: string
        isAdmin: boolean
    }
}

export async function login(username: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Login failed')
    }

    return await response.json()
}

export interface AuthConfig {
    googleClientId: string | null
}

// Ask the server whether Google login is available (and with which client id)
export async function getAuthConfig(): Promise<AuthConfig> {
    try {
        const response = await fetch(`${API_URL}/api/auth/config`)
        if (!response.ok) return { googleClientId: null }
        return await response.json()
    } catch {
        return { googleClientId: null }
    }
}

// Exchange a Google ID token (from Google Identity Services) for our app session
export async function loginWithGoogle(credential: string): Promise<LoginResponse> {
    const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ credential })
    })

    if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.error || 'Google login failed')
    }

    return await response.json()
}

export function getToken(): string | null {
    return localStorage.getItem('auth_token')
}

export function setToken(token: string): void {
    localStorage.setItem('auth_token', token)
}

export function clearToken(): void {
    localStorage.removeItem('auth_token')
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/auth/password`, {
        method: 'PUT',
        headers: getTokenHeader(),
        body: JSON.stringify({ currentPassword, newPassword })
    })

    if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to change password')
    }
}
